import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Bitcoin } from "lucide-react";

// Mapeo CoinGecko ID -> símbolo + color de display
// Las 4 monedas soportadas por TrendIA aparecen primero (BTC, ETH, SOL, ZEC)
const COIN_MAP = [
  { id: "bitcoin", sym: "BTC", color: "text-orange-400 border-orange-400/30 bg-orange-400/5" },
  { id: "ethereum", sym: "ETH", color: "text-cyan-300 border-cyan-300/30 bg-cyan-300/5" },
  { id: "solana", sym: "SOL", color: "text-purple-300 border-purple-300/30 bg-purple-300/5" },
  { id: "zcash", sym: "ZEC", color: "text-yellow-300 border-yellow-300/30 bg-yellow-300/5" },
  { id: "binancecoin", sym: "BNB", color: "text-amber-300 border-amber-300/30 bg-amber-300/5" },
  { id: "ripple", sym: "XRP", color: "text-sky-300 border-sky-300/30 bg-sky-300/5" },
  { id: "cardano", sym: "ADA", color: "text-blue-300 border-blue-300/30 bg-blue-300/5" },
  { id: "dogecoin", sym: "DOGE", color: "text-amber-300 border-amber-300/30 bg-amber-300/5" },
  { id: "polkadot", sym: "DOT", color: "text-pink-300 border-pink-300/30 bg-pink-300/5" },
  { id: "avalanche-2", sym: "AVAX", color: "text-red-300 border-red-300/30 bg-red-300/5" },
  { id: "chainlink", sym: "LINK", color: "text-indigo-300 border-indigo-300/30 bg-indigo-300/5" },
];

// Floating pills muestran SOLO las 4 monedas soportadas por TrendIA
const FLOATING_SYMBOLS = ["BTC", "ETH", "SOL", "ZEC", "BTC", "ETH"];

const formatPrice = (p) => {
  if (p == null) return "—";
  if (p >= 1000) return p.toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (p >= 1) return p.toFixed(2);
  if (p >= 0.01) return p.toFixed(3);
  return p.toFixed(5);
};

export const CryptoHeroBackground = () => {
  const canvasRef = useRef(null);
  const [prices, setPrices] = useState(null);
  const [live, setLive] = useState(false);

  // Fetch real prices from CoinGecko via backend proxy (avoids browser CORS/401 spam)
  useEffect(() => {
    let stopped = false;
    // Use a relative path so the request stays same-origin in production
    // (rebelbtc.com → Vercel rewrites /api/* to the Emergent backend).
    // Previously this used REACT_APP_BACKEND_URL which baked the preview URL
    // into the bundle and caused the price banner to fail silently in prod.
    const fetchPrices = async () => {
      try {
        const ids = COIN_MAP.map((c) => c.id).join(",");
        const url = `/api/prices/simple?ids=${ids}&vs_currencies=usd&include_24hr_change=true`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("bad response");
        const data = await res.json();
        if (!stopped) {
          setPrices(data);
          setLive(true);
        }
      } catch (e) {
        if (!stopped) setLive(false);
      }
    };
    fetchPrices();
    const id = setInterval(fetchPrices, 30000); // refresh every 30s
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let particles = [];
    const NUM = 90;
    const MAX_DIST = 150;
    const mouse = { x: -9999, y: -9999 };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const init = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      particles = [];
      for (let i = 0; i < NUM; i++) {
        const roll = Math.random();
        const c = roll > 0.7 ? "255,140,0" : roll > 0.4 ? "0,229,255" : "168,85,247";
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.35,
          vy: (Math.random() - 0.5) * 0.35,
          r: Math.random() * 1.6 + 0.6,
          c,
        });
      }
    };

    const draw = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);

      for (const p of particles) {
        // mouse repulsion (soft)
        const mdx = p.x - mouse.x;
        const mdy = p.y - mouse.y;
        const md = Math.sqrt(mdx * mdx + mdy * mdy);
        if (md < 120) {
          const f = (120 - md) / 120;
          p.vx += (mdx / md) * f * 0.04;
          p.vy += (mdy / md) * f * 0.04;
        }
        p.vx *= 0.99;
        p.vy *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
      }

      // connection lines (constellation)
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < MAX_DIST) {
            const alpha = (1 - d / MAX_DIST) * 0.22;
            ctx.strokeStyle = `rgba(120,180,255,${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // particles
      for (const p of particles) {
        ctx.fillStyle = `rgba(${p.c}, 0.9)`;
        ctx.shadowColor = `rgba(${p.c}, 0.9)`;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;

      raf = requestAnimationFrame(draw);
    };

    const onMouse = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    resize();
    init();
    draw();
    const onResize = () => {
      resize();
      init();
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", onMouse);
    window.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMouse);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  // Build ticker rows from real prices (duplicated for seamless marquee)
  const coinRows = COIN_MAP.map((c) => {
    const d = prices?.[c.id];
    const price = d?.usd ?? null;
    const chg = d?.usd_24h_change ?? null;
    return {
      sym: c.sym,
      color: c.color,
      price: formatPrice(price),
      chg: chg == null ? "—" : `${chg >= 0 ? "+" : ""}${chg.toFixed(2)}%`,
      up: chg == null ? null : chg >= 0,
    };
  });
  const ticker = [...coinRows, ...coinRows];

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {/* Canvas particle network */}
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full opacity-80" />

      {/* Perspective animated grid floor */}
      <div className="absolute inset-x-0 bottom-0 h-[60%] [perspective:900px]">
        <div className="absolute inset-x-0 bottom-0 top-0 origin-bottom [transform:rotateX(62deg)_translateZ(0)]">
          <div
            className="absolute inset-0 opacity-[0.18]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(0,229,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(168,85,247,0.5) 1px, transparent 1px)",
              backgroundSize: "80px 80px",
              animation: "rebel-gridmove 6s linear infinite",
            }}
          />
        </div>
        {/* Horizon fade */}
        <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black to-transparent" />
      </div>

      {/* Center radial pulse */}
      <motion.div
        className="absolute left-1/2 top-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,140,0,0.18) 0%, rgba(168,85,247,0.08) 40%, transparent 70%)",
        }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Floating crypto pills — drift up */}
      <div className="absolute inset-0">
        {FLOATING_SYMBOLS.map((sym, i) => {
          const left = 8 + ((i * 17) % 86);
          const delay = i * 1.2;
          const duration = 14 + (i % 4) * 4;
          // Hide extra pills on mobile to reduce visual clutter
          const hideOnMobile = i >= 3;
          return (
            <motion.div
              key={sym + i}
              className={`absolute flex items-center gap-1.5 rounded-full border border-white/10 bg-black/40 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white/70 backdrop-blur-md ${
                hideOnMobile ? "hidden md:flex" : ""
              }`}
              style={{ left: `${left}%`, bottom: "-40px" }}
              initial={{ y: 0, opacity: 0 }}
              animate={{ y: -(window.innerHeight + 200), opacity: [0, 1, 1, 0] }}
              transition={{
                duration,
                delay,
                repeat: Infinity,
                ease: "linear",
                times: [0, 0.1, 0.85, 1],
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(0,229,255,0.9)]" />
              {sym}
            </motion.div>
          );
        })}
      </div>

      {/* 3D rotating Bitcoin coin (top-left) */}
      <motion.div
        className="absolute right-[8%] top-[18%] hidden md:block"
        animate={{ y: [0, -18, 0], rotate: [0, 360] }}
        transition={{
          y: { duration: 5, repeat: Infinity, ease: "easeInOut" },
          rotate: { duration: 14, repeat: Infinity, ease: "linear" },
        }}
      >
        <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-orange-700 shadow-[0_0_60px_rgba(255,140,0,0.5)] ring-2 ring-orange-200/30">
          <Bitcoin className="h-10 w-10 text-black/90" strokeWidth={2.5} />
          <span className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white/40 to-transparent opacity-60" />
        </div>
      </motion.div>

      {/* Smaller floating coin (bottom-left) */}
      <motion.div
        className="absolute left-[6%] top-[55%] hidden md:block"
        animate={{ y: [0, 14, 0], rotate: [0, -360] }}
        transition={{
          y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
          rotate: { duration: 18, repeat: Infinity, ease: "linear" },
        }}
      >
        <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-cyan-300 via-purple-500 to-fuchsia-600 shadow-[0_0_50px_rgba(168,85,247,0.5)] ring-2 ring-cyan-200/30">
          <span className="font-display text-xl font-black text-black/85">Ξ</span>
          <span className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white/40 to-transparent opacity-50" />
        </div>
      </motion.div>

      {/* Live ticker tape (top edge, below navbar) */}
      <div className="pointer-events-auto absolute inset-x-0 top-20 z-10 overflow-hidden border-y border-white/5 bg-black/40 py-2 backdrop-blur-md md:top-24">
        <div className="flex items-center">
          {/* LIVE badge fixed left */}
          <div className="z-20 hidden shrink-0 items-center gap-1.5 border-r border-white/10 bg-black/70 px-3 py-1 md:flex">
            <span className="relative flex h-1.5 w-1.5">
              {live && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              )}
              <span
                className={`relative inline-flex h-1.5 w-1.5 rounded-full ${
                  live ? "bg-emerald-500" : "bg-zinc-500"
                }`}
              />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
              {live ? "Live · CoinGecko" : "Loading…"}
            </span>
          </div>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max gap-10 whitespace-nowrap pl-6 [animation:rebel-marquee_55s_linear_infinite]">
              {ticker.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] font-bold tracking-widest md:text-xs">
                  <span className={`rounded-md border px-1.5 py-0.5 ${c.color}`}>{c.sym}</span>
                  <span className="font-display text-white/85">${c.price}</span>
                  {c.up === null ? (
                    <span className="text-white/30">—</span>
                  ) : (
                    <span className={c.up ? "text-emerald-400" : "text-red-400"}>
                      {c.up ? "▲" : "▼"} {c.chg}
                    </span>
                  )}
                  <span className="text-white/15">·</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scanlines / subtle CRT effect */}
      <div
        className="absolute inset-0 opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 3px)",
        }}
      />
    </div>
  );
};
